<template>
  <v-app>
    <template v-if="configLoadFailed">
      <v-container class="d-flex align-center justify-center fill-height">
        <div class="text-center">
          <p class="mb-4">{{ $t('appErrors.configLoadFailed') }}</p>
          <v-btn color="primary" @click="retryLoad">{{ $t('appErrors.configLoadFailedRetry') }}</v-btn>
        </div>
      </v-container>
    </template>
    <template v-else>
      <RouterView />
    </template>
    <RDPToast />
  </v-app>
</template>

<script setup lang="ts">
import { onBeforeMount, onMounted, ref } from 'vue';
import { useAuthStore } from '@/store/useAuthStore';
import { useShoppingCartStore } from '@/store/useShoppingCartStore';
import loadSystemConfiguration, { loadPublicSystemConfiguration } from '@/utils/systemConfiguration';
import { loadSupportedLocales } from '@/utils/i18n';
import RDPToast from '@/components/common/RDPToast.vue';
import accountService from './services/api/accountService';

const authStore = useAuthStore();
const shoppingCartStore = useShoppingCartStore();
const configLoadFailed = ref(false);

const loadConfigAndCart = async (): Promise<boolean> => {
  const success = await loadSystemConfiguration();
  if (!success) return false;

  const accountCartLifetime = await accountService.getCustomerAccountCartLifetime();
  if (accountCartLifetime.data.cartLifetime) {
    shoppingCartStore.setExpirationTime(accountCartLifetime.data.cartLifetime);
    shoppingCartStore.setCartLifetimeTimer();
  }
  return true;
};

const retryLoad = async () => {
  configLoadFailed.value = false;
  if (!(await loadConfigAndCart())) {
    configLoadFailed.value = true;
  }
};

onBeforeMount(async () => {
  await loadSupportedLocales();
  if (authStore.logged && !authStore.tokenExpired) {
    if (!(await loadConfigAndCart())) {
      configLoadFailed.value = true;
    }
  } else {
    await loadPublicSystemConfiguration();
  }
});

onMounted(() => {
  // this event is only fired when persisted vuex storage changes in other tabs
  // it means that on login or logout, the other tabs are refreshed
  // this prevents from two different users being logged-in in two different tabs - see RDP-330
  globalThis.addEventListener('storage', event => {
    const oldValue = JSON.parse(event.oldValue as string);
    const newValue = JSON.parse(event.newValue as string);

    // reload the page only on customer change, not on any change of the storage - @see RDP-501
    // if customer changed, it means new user is logged in or sub-account was switched
    // compare the customers on their email (aka username) - @see RDP-1063
    if (oldValue && newValue && oldValue.auth?.username !== newValue.auth?.username) {
      // timeout is needed otherwise the cypress tests won't work
      setTimeout(() => location.reload(), 100);
    }
  });
});
</script>

<style lang="scss">
.theme--light.v-btn.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) .v-icon {
  color: #757575 !important;
}

.row {
  margin: 0 -12px;
}

html {
  overflow-y: auto !important;
}
</style>
